// Capacitor-adapters voor de iPhone-app (apps/mobile, ADR-0001 "Native (F3)"). Alleen de ouderapp
// importeert dit (subpad @amumin/platform/capacitor); de kiosk nooit, zodat het bord er geen byte
// van meekrijgt.
//
// Geen @capacitor/core in de bundel: de iOS-schil injecteert zelf `window.Capacitor` (native-bridge.js
// en per plugin een stukje JS, als WKUserScript bij documentstart). Daar praten we rechtstreeks mee
// via nativePromise. In een gewone browser bestaat window.Capacitor niet en blijft alles bij de
// web-implementaties uit index.ts.

import type { HapticsAdapter, OpenExternalAdapter, Platform } from './index.js';

/** Het deel van window.Capacitor dat de native schil levert (native-bridge.js). */
export interface CapacitorBridge {
  isNativePlatform(): boolean;
  getPlatform(): string;
  isPluginAvailable(name: string): boolean;
  nativePromise(
    plugin: string,
    method: string,
    options?: Record<string, unknown>,
  ): Promise<unknown>;
}

type WindowLike = { Capacitor?: Partial<CapacitorBridge> } | undefined;

function currentWindow(): WindowLike {
  return typeof window !== 'undefined' ? (window as unknown as WindowLike) : undefined;
}

/**
 * De native brug, of null in een gewone browser. Veilige detectie: alleen als de schil
 * isNativePlatform() en nativePromise() echt levert (een pagina kan zelf geen native brug maken).
 */
export function capacitorBridge(win: WindowLike = currentWindow()): CapacitorBridge | null {
  const cap = win?.Capacitor;
  if (!cap || typeof cap.isNativePlatform !== 'function') return null;
  if (typeof cap.nativePromise !== 'function' || typeof cap.isPluginAvailable !== 'function') {
    return null;
  }
  try {
    return cap.isNativePlatform() ? (cap as CapacitorBridge) : null;
  } catch {
    return null;
  }
}

/** Draait de ouderapp in de iPhone-app (en niet in Safari)? */
export function isNativeApp(win: WindowLike = currentWindow()): boolean {
  return capacitorBridge(win) !== null;
}

/**
 * Roept een plugin aan als die er is. Geeft false als de plugin ontbreekt (oude build van de
 * schil) of faalt; gooit nooit, zodat de app gewoon doorwerkt.
 */
export function callNative(
  bridge: CapacitorBridge,
  plugin: string,
  method: string,
  options?: Record<string, unknown>,
): boolean {
  try {
    if (!bridge.isPluginAvailable(plugin)) return false;
    bridge.nativePromise(plugin, method, options).catch(() => undefined);
    return true;
  } catch {
    return false;
  }
}

/** Trillen via de Taptic Engine (iOS Safari kent navigator.vibrate niet). */
export function nativeHaptics(bridge: CapacitorBridge): HapticsAdapter {
  return {
    impact(style) {
      return callNative(bridge, 'Haptics', 'impact', {
        style: style === 'strong' ? 'MEDIUM' : 'LIGHT',
      });
    },
  };
}

/**
 * Vertaalt de trilpatronen van ui-core (haptics.ts: 10 ms tik, 20 ms sterk, [15, 60, 15] fout) naar
 * de iOS-gevoelstypen. Voor setTapDriver in ui-core.
 */
export function nativeTapDriver(
  bridge: CapacitorBridge,
): (pattern: number | ReadonlyArray<number>) => boolean {
  const haptics = nativeHaptics(bridge);
  return (pattern) => {
    if (typeof pattern !== 'number') {
      return callNative(bridge, 'Haptics', 'notification', { type: 'ERROR' });
    }
    return haptics.impact(pattern > 12 ? 'strong' : 'light');
  };
}

/**
 * Externe links in Safari, niet in de WebView van de app. Een nieuw venster opent de schil zelf in
 * Safari (Capacitor iOS, WebViewDelegationHandler: createWebViewWith en elke navigatie buiten
 * server.url gaan naar UIApplication.open). Zo ook gewone <a href> naar een ander domein, mailto:
 * en tel:; daar is geen plugin voor nodig.
 */
export function nativeOpenExternal(_bridge: CapacitorBridge): OpenExternalAdapter {
  return {
    open(url) {
      if (typeof window !== 'undefined') window.open(url, '_blank', 'noopener');
    },
  };
}

/** Het platform met native adapters waar de schil ze levert; anders ongewijzigd. */
export function withNative(base: Platform, win: WindowLike = currentWindow()): Platform {
  const bridge = capacitorBridge(win);
  if (!bridge) return base;
  return { ...base, haptics: nativeHaptics(bridge), openExternal: nativeOpenExternal(bridge) };
}

export interface NativeShellOptions {
  /** Element met data-theme="dark" bij donkere weergave (state/colorScheme.ts). */
  root?: HTMLElement;
}

/**
 * Eén keer bij het starten van de ouderapp (main.tsx), na de eerste render. In een gewone browser
 * doet dit niets. In de app:
 *  - `data-native="ios"` op <html>, zodat CSS iets anders kan doen dan in Safari;
 *  - de statusbalk volgt licht/donker (DARK = lichte tekst op een donkere achtergrond);
 *  - het opstartscherm gaat weg na de eerste tekening.
 * Geeft een opruimer terug.
 */
export function installNativeShell(options: NativeShellOptions = {}): () => void {
  const bridge = capacitorBridge();
  if (!bridge || typeof document === 'undefined') return () => undefined;
  const root = options.root ?? document.documentElement;
  root.setAttribute('data-native', bridge.getPlatform());

  const syncStatusBar = (): void => {
    const dark = root.getAttribute('data-theme') === 'dark';
    callNative(bridge, 'StatusBar', 'setStyle', { style: dark ? 'DARK' : 'LIGHT' });
  };
  syncStatusBar();
  const observer =
    typeof MutationObserver === 'function' ? new MutationObserver(syncStatusBar) : null;
  observer?.observe(root, { attributes: true, attributeFilter: ['data-theme'] });

  // Twee frames: de eerste tekening staat echt op het scherm voordat het opstartscherm vervaagt.
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      callNative(bridge, 'SplashScreen', 'hide', { fadeOutDuration: 200 });
    });
  });

  return () => {
    observer?.disconnect();
    root.removeAttribute('data-native');
  };
}
